import React from "react";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { BADGE_COLORS, getBadgeStyles, getAlmacenColor } from "./badgeColors";

/**
 * Componente para mostrar stock por almacén
 * Soporta diferentes variantes visuales según el contexto
 */
const StockDisplay = ({ stock = [], getNombreAlmacen, variant = "compact", maxVisible = 3 }) => {
    if (!stock || stock.length === 0) {
        return (
            <Typography sx={{ fontSize: 12, color: "#6c757d", fontStyle: "italic" }}>
                Sin stock
            </Typography>
        );
    }

    // Ordenar: primero los que tienen stock, luego alfabéticamente por almacén
    const stockOrdenado = [...stock].sort((a, b) => {
        // Primero: separar por stock disponible
        if (a.cantidad > 0 && b.cantidad === 0) return -1;
        if (a.cantidad === 0 && b.cantidad > 0) return 1;
        // Dentro de cada grupo: ordenar alfabéticamente por código de almacén
        return a.almacen.localeCompare(b.almacen);
    });

    // Filtrar solo almacenes con stock para algunas variantes
    const stockConCantidad = stockOrdenado.filter(s => s.cantidad > 0);
    const stockAMostrar = variant === "compact" ? stockConCantidad.slice(0, maxVisible) : stockOrdenado;
    const stockRestante = stockConCantidad.length - maxVisible;

    // Variant: compact (para DataGrid)
    if (variant === "compact") {
        if (stockConCantidad.length === 0) {
            return (
                <Chip
                    label="Sin stock"
                    size="small"
                    sx={getBadgeStyles(BADGE_COLORS.GRAY)}
                />
            );
        }

        return (
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
                {stockAMostrar.map((s, idx) => {
                    const nombreAlmacen = getNombreAlmacen ? getNombreAlmacen(s.almacen) : s.almacen;
                    const nombreCorto = nombreAlmacen.startsWith("Nave ")
                        ? "N" + nombreAlmacen.split(" ")[1]
                        : nombreAlmacen;
                    const colorAlmacen = getAlmacenColor(s.almacen);
                    return (
                        <Tooltip key={idx} title={`${nombreAlmacen}: ${s.cantidad} uds`} arrow>
                            <Chip
                                label={`${nombreCorto}: ${s.cantidad}`}
                                size="small"
                                sx={getBadgeStyles(colorAlmacen)}
                            />
                        </Tooltip>
                    );
                })}
                {stockRestante > 0 && (
                    <Tooltip
                        title={stockConCantidad
                            .slice(maxVisible)
                            .map(s => {
                                const nombreAlmacen = getNombreAlmacen ? getNombreAlmacen(s.almacen) : s.almacen;
                                return `${nombreAlmacen}: ${s.cantidad} uds`;
                            })
                            .join(", ")}
                        arrow
                    >
                        <Chip
                            label={`+${stockRestante}`}
                            size="small"
                            sx={getBadgeStyles(BADGE_COLORS.GRAY)}
                        />
                    </Tooltip>
                )}
            </Box>
        );
    }

    // Variant: expanded (para modales)
    if (variant === "expanded") {
        return (
            <Stack spacing={1}>
                {stockAMostrar.map((s, idx) => {
                    const nombreAlmacen = getNombreAlmacen ? getNombreAlmacen(s.almacen) : s.almacen;
                    const tieneStock = s.cantidad > 0;
                    return (
                        <Box
                            key={idx}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: 1,
                                backgroundColor: tieneStock ? "#e9ecef" : "#f8f9fa",
                                borderRadius: 1,
                                border: `1px solid ${tieneStock ? "#dee2e6" : "#dee2e6"}`,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <WarehouseIcon
                                    sx={{
                                        fontSize: 18,
                                        color: tieneStock ? "#495057" : "#6c757d",
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: tieneStock ? "#212529" : "#495057",
                                    }}
                                >
                                    {nombreAlmacen}
                                </Typography>
                            </Box>
                            <Chip
                                label={`${s.cantidad} uds`}
                                size="small"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: 12,
                                    backgroundColor: tieneStock ? "#495057" : "#adb5bd",
                                    color: "#fff",
                                }}
                            />
                        </Box>
                    );
                })}
            </Stack>
        );
    }

    // Variant: mobile (para cards mobile)
    if (variant === "mobile") {
        if (stockConCantidad.length === 0) {
            return (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: 12,
                        color: "#842029",
                    }}
                >
                    <WarehouseIcon sx={{ fontSize: 14 }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                        Sin stock disponible
                    </Typography>
                </Box>
            );
        }

        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: 12,
                    color: "#495057",
                }}
            >
                <WarehouseIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                    Stock: {stockConCantidad.map(s => {
                        const nombreAlmacen = getNombreAlmacen ? getNombreAlmacen(s.almacen) : s.almacen;
                        const nombreCorto = nombreAlmacen.startsWith("Nave ")
                            ? "N" + nombreAlmacen.split(" ")[1]
                            : nombreAlmacen;
                        return `${nombreCorto}(${s.cantidad})`;
                    }).join(", ")}
                </Typography>
            </Box>
        );
    }

    return null;
};

export default StockDisplay;
